import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ConnectionsBasketChip from './ConnectionsBasketChip';
import ConnectionsBasketTile from './ConnectionsBasketTile';
import ConnectionsUnfiledTopicPill from './ConnectionsUnfiledTopicPill';
import DueSession from './DueSession';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { DUE_DAILY_CAP } from '../../store/workspacesSlice';

const ALL_TAGS = 'all';
const GLANCE_STRIP_CAP = 5;
const BASKETS_ROW_CAP = 4;

const sortByRecency = (items) =>
  [...(items || [])].sort((a, b) => {
    const timeA = a?.updated_at
      ? new Date(a.updated_at).getTime()
      : a?.created_at
        ? new Date(a.created_at).getTime()
        : 0;
    const timeB = b?.updated_at
      ? new Date(b.updated_at).getTime()
      : b?.created_at
        ? new Date(b.created_at).getTime()
        : 0;
    return timeB - timeA;
  });

const sortBasketsForDisplay = (items) => {
  const byRecency = sortByRecency(items);
  return [...byRecency].sort((a, b) => (b?.is_pinned === true) - (a?.is_pinned === true));
};

/**
 * Connections default (no-query) home: Due / Baskets / Unfiled toggle,
 * glance strips, and in-place drill-ins. Due tab hosts DueSession inline.
 */
const ConnectionsDefaultHome = ({
  defaultView,
  defaultTab,
  baskets = [],
  allBaskets = [],
  allTopics = [],
  unfiledTopics = [],
  dueTopics = [],
  workspaceId,
  masteryFilterActive = false,
  masteryFilterLabel = '',
  unfiledTagFilter = ALL_TAGS,
  dueDoneIds,
  dueRoundKey = 0,
  onDueTopicDone,
  onStartNextDueRound,
  onDefaultTabChange,
  onSeeAll,
  onBackToLanding,
  onBasketOpen,
  onTogglePin,
  onUnfiledOpen,
  onUnfiledTagFilterChange,
  onClearMasteryFilter,
  basketRowOrderIds = null,
  openedBasketId = null,
  onBasketRowTileOpen,
}) => {
  const basketCount = baskets.length;
  const unfiledCount = unfiledTopics.length;
  const dueCount = dueTopics.length;
  const allBasketCount = allBaskets.length;

  const handleClearMasteryFromChip = () => {
    captureConnectionsEvent('mastery_filter_cleared', { source: 'chip' });
    onClearMasteryFilter?.();
  };

  const handleClearMasteryFromEmpty = () => {
    captureConnectionsEvent('mastery_filter_cleared', { source: 'empty_state' });
    onClearMasteryFilter?.();
  };
  const [hadDue, setHadDue] = useState(dueCount > 0);
  useEffect(() => {
    if (dueCount > 0) setHadDue(true);
  }, [dueCount]);
  useEffect(() => {
    setHadDue(dueCount > 0);
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  const dueShownCount = Math.min(dueCount, DUE_DAILY_CAP);

  // Stable row order from page ids; drop missing; filter via `baskets` prop.
  const orderedRowBaskets = useMemo(() => {
    if (!basketRowOrderIds?.length) {
      return sortBasketsForDisplay(baskets);
    }
    const byId = new Map((baskets || []).map((basket) => [basket.id, basket]));
    return basketRowOrderIds
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [basketRowOrderIds, baskets]);

  const rowGlanceBaskets = useMemo(
    () => orderedRowBaskets.slice(0, BASKETS_ROW_CAP),
    [orderedRowBaskets]
  );

  const glanceUnfiled = useMemo(
    () => sortByRecency(unfiledTopics).slice(0, GLANCE_STRIP_CAP),
    [unfiledTopics]
  );

  const dueQueueIds = useMemo(() => {
    const ids = dueTopics.slice(0, DUE_DAILY_CAP).map((t) => t.id);
    dueDoneIds?.forEach((id) => {
      if (!ids.includes(id)) ids.push(id);
    });
    return ids;
  }, [dueTopics, dueDoneIds]);

  const basketsRowRef = useRef(null);
  const [showBasketsOverflowCue, setShowBasketsOverflowCue] = useState(false);

  // Fade the right edge when more row content (tiles or See all) is off-screen.
  const updateBasketsOverflowCue = useCallback(() => {
    const el = basketsRowRef.current;
    if (!el) {
      setShowBasketsOverflowCue(false);
      return;
    }
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setShowBasketsOverflowCue(canScrollRight);
  }, []);

  useEffect(() => {
    if (defaultTab !== 'baskets') return undefined;
    const el = basketsRowRef.current;
    if (!el) return undefined;

    updateBasketsOverflowCue();
    el.addEventListener('scroll', updateBasketsOverflowCue, { passive: true });
    window.addEventListener('resize', updateBasketsOverflowCue);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateBasketsOverflowCue)
        : null;
    resizeObserver?.observe(el);

    return () => {
      el.removeEventListener('scroll', updateBasketsOverflowCue);
      window.removeEventListener('resize', updateBasketsOverflowCue);
      resizeObserver?.disconnect();
    };
  }, [
    defaultTab,
    updateBasketsOverflowCue,
    rowGlanceBaskets,
    basketCount,
  ]);

  const unfiledTagOptions = useMemo(() => {
    const byId = new Map();
    unfiledTopics.forEach((topic) => {
      const tag = topic?.tag || topic?.tags;
      const tagId = topic?.tag_id || tag?.id;
      if (!tagId || !tag?.name) return;
      const existing = byId.get(tagId);
      if (existing) {
        existing.count += 1;
      } else {
        byId.set(tagId, {
          id: tagId,
          name: tag.name,
          color: tag.color,
          count: 1,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [unfiledTopics]);

  const filteredUnfiled = useMemo(() => {
    if (unfiledTagFilter === ALL_TAGS) return unfiledTopics;
    return unfiledTopics.filter((topic) => {
      const tag = topic?.tag || topic?.tags;
      const tagId = topic?.tag_id || tag?.id;
      return tagId === unfiledTagFilter;
    });
  }, [unfiledTopics, unfiledTagFilter]);

  useEffect(() => {
    if (defaultTab !== 'due' || !workspaceId) return;
    captureConnectionsEvent('due_for_review_tab_shown', {
      workspace_id: workspaceId,
      due_count_shown: dueShownCount,
      due_count_total: dueCount,
    });
  }, [defaultTab, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps -- fire when Due becomes active, not on every count tick

  const handleTabChange = (tab) => {
    if (tab === defaultTab) return;
    onDefaultTabChange?.(tab);
    captureConnectionsEvent('connections_default_tab_switched', {
      tab,
      workspace_id: workspaceId,
    });
  };

  const handleSeeAll = (kind) => {
    onSeeAll?.(kind, 'strip');
  };

  const handleTagClick = (tagKey) => {
    onUnfiledTagFilterChange?.(tagKey);
    const tagLabel =
      tagKey === ALL_TAGS
        ? 'all'
        : unfiledTagOptions.find((t) => t.id === tagKey)?.name || String(tagKey);
    captureConnectionsEvent('connections_unfiled_tag_filtered', {
      tag: tagLabel,
      workspace_id: workspaceId,
    });
  };

  if (defaultView === 'allBaskets') {
    return (
      <div className="w-full max-w-[700px] space-y-4">
        <button
          type="button"
          onClick={onBackToLanding}
          className="text-sm text-neutral-600 underline hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded"
        >
          Back
        </button>
        <h2 className="text-base font-semibold text-neutral-900">
          All baskets · {basketCount}
        </h2>
        {masteryFilterActive && basketCount > 0 && (
          <MasteryFilterChip
            label={masteryFilterLabel}
            basketCount={basketCount}
            totalCount={allBasketCount}
            onClear={handleClearMasteryFromChip}
          />
        )}
        {basketCount === 0 ? (
          <EmptyMessage
            masteryFilterActive={masteryFilterActive}
            kind="baskets"
            onClearMasteryFilter={handleClearMasteryFromEmpty}
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortBasketsForDisplay(baskets).map((basket) => (
              <ConnectionsBasketChip
                key={basket.id}
                basket={basket}
                onClick={onBasketOpen}
                onTogglePin={onTogglePin}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (defaultView === 'allUnfiled') {
    return (
      <div className="w-full max-w-[700px] space-y-4">
        <button
          type="button"
          onClick={onBackToLanding}
          className="text-sm text-neutral-600 underline hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded"
        >
          Back
        </button>
        <h2 className="text-base font-semibold text-neutral-900">
          Unfiled topics · {unfiledCount}
        </h2>

        <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
          <div className="flex gap-2 min-w-min pb-1" role="group" aria-label="Tag filter">
            <TagFilterButton
              active={unfiledTagFilter === ALL_TAGS}
              onClick={() => handleTagClick(ALL_TAGS)}
              count={unfiledCount}
            >
              All tags
            </TagFilterButton>
            {unfiledTagOptions.map((tag) => (
              <TagFilterButton
                key={tag.id}
                active={unfiledTagFilter === tag.id}
                onClick={() => handleTagClick(tag.id)}
                count={tag.count}
              >
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color || '#94A3B8' }}
                    aria-hidden="true"
                  />
                  {tag.name}
                </span>
              </TagFilterButton>
            ))}
          </div>
        </div>

        {filteredUnfiled.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">
            {unfiledCount === 0
              ? "Topics you haven't placed in a basket show up here."
              : 'No unfiled topics match this tag.'}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {filteredUnfiled.map((topic) => (
              <ConnectionsUnfiledTopicPill
                key={topic.id}
                topic={topic}
                onClick={onUnfiledOpen}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Landing
  return (
    <div className="w-full max-w-[700px] space-y-4">
      <div
        className="inline-flex rounded-xl border-2 border-neutral-200 bg-neutral-100 p-0.5"
        role="tablist"
        aria-label="Browse due topics, baskets, or unfiled topics"
      >
        <button
          type="button"
          role="tab"
          aria-selected={defaultTab === 'due'}
          onClick={() => handleTabChange('due')}
          className={`px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cranberry-700 ${
            defaultTab === 'due'
              ? 'bg-white font-semibold text-neutral-900 shadow-sm'
              : 'font-medium text-neutral-600'
          }`}
        >
          Due today · {dueShownCount}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={defaultTab === 'baskets'}
          onClick={() => handleTabChange('baskets')}
          className={`px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cranberry-700 ${
            defaultTab === 'baskets'
              ? 'bg-white font-semibold text-neutral-900 shadow-sm'
              : 'font-medium text-neutral-600'
          }`}
        >
          Baskets · {basketCount}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={defaultTab === 'unfiled'}
          onClick={() => handleTabChange('unfiled')}
          className={`px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cranberry-700 ${
            defaultTab === 'unfiled'
              ? 'bg-white font-semibold text-neutral-900 shadow-sm'
              : 'font-medium text-neutral-600'
          }`}
        >
          Unfiled · {unfiledCount}
        </button>
      </div>

      {defaultTab === 'due' && (
        <div className="space-y-2">
          {dueCount === 0 && !hadDue && (dueDoneIds?.size ?? 0) === 0 ? (
            <EmptyMessage kind="due" />
          ) : (
            <DueSession
              key={`${workspaceId}-${dueRoundKey}`}
              queueIds={dueQueueIds}
              startIndex={0}
              baskets={allBaskets}
              topics={allTopics}
              workspaceId={workspaceId}
              doneIds={dueDoneIds}
              remainingDueCount={dueCount}
              onTopicDone={onDueTopicDone}
              onStartNextRound={onStartNextDueRound}
              onExit={() => handleTabChange('baskets')}
            />
          )}
        </div>
      )}

      {defaultTab === 'baskets' && (
        <div className="space-y-2">
          {masteryFilterActive && basketCount > 0 && (
            <MasteryFilterChip
              label={masteryFilterLabel}
              basketCount={basketCount}
              totalCount={allBasketCount}
              onClear={handleClearMasteryFromChip}
            />
          )}
          {basketCount === 0 && !masteryFilterActive ? (
            <EmptyMessage kind="baskets" masteryFilterActive={false} />
          ) : basketCount === 0 && masteryFilterActive ? (
            <EmptyMessage
              kind="baskets"
              masteryFilterActive
              onClearMasteryFilter={handleClearMasteryFromEmpty}
            />
          ) : (
            <div className="relative">
              <div
                ref={basketsRowRef}
                className="flex items-center gap-2 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-1"
                aria-label="Baskets"
                role="list"
              >
                {rowGlanceBaskets.map((basket, index) => (
                  <div key={basket.id} role="listitem" className="snap-start shrink-0">
                    <ConnectionsBasketTile
                      basket={basket}
                      selected={basket.id === openedBasketId}
                      onClick={() => onBasketRowTileOpen?.(basket, index)}
                    />
                  </div>
                ))}
                {basketCount > BASKETS_ROW_CAP && (
                  <button
                    type="button"
                    onClick={() => handleSeeAll('baskets')}
                    className="inline-flex items-center shrink-0 snap-start text-sm font-medium text-cranberry-700 hover:text-cranberry-600 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded px-1"
                  >
                    See all {basketCount} →
                  </button>
                )}
              </div>
              {showBasketsOverflowCue && (
                <div
                  className="pointer-events-none absolute right-0 top-0 bottom-1 w-12 bg-gradient-to-l from-white via-white/90 to-transparent"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
        </div>
      )}

      {defaultTab === 'unfiled' && (
        <div className="space-y-2">
          {unfiledCount === 0 ? (
            <EmptyMessage kind="unfiled" />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {glanceUnfiled.map((topic) => (
                <ConnectionsUnfiledTopicPill
                  key={topic.id}
                  topic={topic}
                  onClick={onUnfiledOpen}
                />
              ))}
              {unfiledCount > GLANCE_STRIP_CAP && (
                <button
                  type="button"
                  onClick={() => handleSeeAll('unfiled')}
                  className="inline-flex items-center shrink-0 text-sm font-medium text-cranberry-700 hover:text-cranberry-600 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded px-1"
                >
                  See all {unfiledCount} →
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MasteryFilterChip = ({ label, basketCount, totalCount, onClear }) => (
  <div className="flex flex-wrap items-center gap-2 sm:gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2">
    <p className="text-sm text-neutral-600 min-w-0 flex-1">
      Showing {label} mastery · {basketCount} of {totalCount} baskets
    </p>
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-2 text-sm font-medium text-cranberry-700 hover:text-cranberry-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cranberry-700 focus:ring-offset-1"
    >
      Show all
    </button>
  </div>
);

const EmptyMessage = ({ kind, masteryFilterActive, onClearMasteryFilter }) => {
  if (kind === 'due') {
    return (
      <p className="text-sm text-neutral-500 py-4">
        Nothing due right now. Topics you&apos;ve recalled come back here the next day until they&apos;re ripe.
      </p>
    );
  }
  if (kind === 'unfiled') {
    return (
      <p className="text-sm text-neutral-500 py-4">
        Topics you haven't placed in a basket show up here.
      </p>
    );
  }
  if (masteryFilterActive) {
    return (
      <div className="py-4 space-y-3">
        <p className="text-sm text-neutral-500">
          No baskets in this mastery range yet.
        </p>
        <button
          type="button"
          onClick={onClearMasteryFilter}
          className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 text-sm font-medium text-cranberry-700 hover:text-cranberry-600 rounded-lg border-2 border-neutral-900 focus:outline-none focus:ring-2 focus:ring-cranberry-700 focus:ring-offset-1"
        >
          Show all baskets
        </button>
      </div>
    );
  }
  return (
    <p className="text-sm text-neutral-500 py-4">
      No baskets yet. Start with a topic and use its difficult detail to create a basket.
    </p>
  );
};

const TagFilterButton = ({ active, onClick, count, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-cranberry-700 ${
      active
        ? 'border-neutral-900 bg-white font-semibold text-neutral-900'
        : 'border-transparent bg-neutral-100 font-medium text-neutral-600 hover:bg-white hover:border-neutral-300'
    }`}
    aria-pressed={active}
  >
    {children}
    <span
      className={`px-1.5 py-0.5 text-xs rounded-full tabular-nums ${
        active ? 'bg-butter-500 text-neutral-900' : 'bg-neutral-200 text-neutral-600'
      }`}
    >
      {count}
    </span>
  </button>
);

export default ConnectionsDefaultHome;
